import { inject, observer } from "mobx-react";
import moment from "moment";
import { FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import ExternalUserManagementStore from "../../../modules/Admin/ExternalUserManagement/store";
import { SSOExternalUserGridListDto } from "../../../modules/Admin/ExternalUserManagement/types";
import LoginAsUserStore from "../../../modules/Admin/LoginAsUser/store";
import { SSOLoginAsUserGridListDto } from "../../../modules/Admin/LoginAsUser/types";
import { Roles } from "../../../modules/Account/AccountLogin/types";
import { getActiveUser } from "../../../utils/SsoSession";
import { REDESIGN_NS } from "../../i18n";
import DataTable, { DataTableColumn } from "../../ui/DataTable";
import {
  IconLogin,
  IconPencil,
  IconPlus,
  IconTrash,
  IconUserPlus,
} from "../../ui/icons";
import { ConfirmModal } from "../../ui/Modal";
import ExternalUserFormModal from "./ExternalUserFormModal";
import ProxyLoginModal from "./ProxyLoginModal";
import RoleAssignmentModal from "./RoleAssignmentModal";
import "./role-assignment.css";

type Tab = "securitas" | "external";

interface Props {
  LoginAsUserStore?: LoginAsUserStore;
  ExternalUserManagementStore?: ExternalUserManagementStore;
}

/**
 * Harici kullanıcı yönetimi de bu sayfada. Liste `LoginAsUserStore.allExternal` ile kalıyor,
 * `ExternalUserManagementStore.all` ile değiştirilmedi: yalnızca `allExternal` isteğe açık
 * oturumların `sessionKeys`ini ekliyor ve değiştirmek vekaleten girişi sessizce bozardı.
 * Mutasyonlar için ikinci store enjekte edilir.
 */
const LoginAsUserView: FC<Props> = inject(
  "LoginAsUserStore",
  "ExternalUserManagementStore"
)(
  observer(
    ({
      LoginAsUserStore: store,
      ExternalUserManagementStore: externalStore,
    }: Props) => {
    const { t } = useTranslation(REDESIGN_NS);

    const [tab, setTab] = useState<Tab>("securitas");
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<SSOLoginAsUserGridListDto>();
    const [selectedExternal, setSelectedExternal] =
      useState<SSOExternalUserGridListDto>();

    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editTarget, setEditTarget] = useState<SSOExternalUserGridListDto>();
    const [roleUser, setRoleUser] = useState<SSOExternalUserGridListDto>();
    const [pendingDelete, setPendingDelete] = useState<SSOExternalUserGridListDto>();
    const [isDeleting, setIsDeleting] = useState(false);
    /* Store mutasyondan sonra kendi başına 1. sayfayı çekiyor; tablo bu token
       ile hizalanmazsa gösterilen veri ile sayfa numarası ayrışır. */
    const [reloadToken, setReloadToken] = useState(0);
    const reload = () => setReloadToken((token) => token + 1);

    /* Sayfa zaten yalnızca admin menüsünde, ama menüyü gizlemek route'u
       korumuyor (ProtectedRoute permission prop'unu okumuyor). Yönetim
       butonları bu yüzden ayrıca kısıtlanır; uçlar da backend'de admin. */
    const isAdmin = (getActiveUser()?.userRoles || []).some(
      (role) => role?.toString() === Roles.admin.toString()
    );

    const confirmDelete = async () => {
      if (!pendingDelete || !externalStore) return;
      setIsDeleting(true);
      try {
        await externalStore.delete(pendingDelete.id);
        setPendingDelete(undefined);
        reload();
      } finally {
        setIsDeleting(false);
      }
    };

    const openSecuritas = (row: SSOLoginAsUserGridListDto) => {
      setSelectedUser(row);
      setSelectedExternal(undefined);
      setIsModalOpen(true);
    };

    const openExternal = (row: SSOExternalUserGridListDto) => {
      setSelectedExternal(row);
      setSelectedUser(undefined);
      setIsModalOpen(true);
    };

    const loginButton = (onClick: () => void) => (
      <div className="sso-rd__table-actions">
        <button
          type="button"
          className="sso-rd__icon-btn sso-rd__icon-btn--login"
          onClick={onClick}
          title={t("Login as this user")}
          aria-label={t("Login as this user")}
        >
          <IconLogin size={16} />
        </button>
      </div>
    );

    const securitasColumns: DataTableColumn<SSOLoginAsUserGridListDto>[] = useMemo(
      () => [
        {
          key: "actions",
          header: t("ACTIONS"),
          width: 92,
          render: (row) => loginButton(() => openSecuritas(row)),
        },
        {
          key: "personnelId",
          header: t("PERSONNEL ID"),
          sortable: true,
          render: (row) => row.personnelId,
        },
        {
          key: "name",
          header: t("FULL NAME"),
          sortable: true,
          render: (row) => <span className="sso-rd__table-strong">{row.name}</span>,
        },
        { key: "email", header: t("EMAIL"), sortable: true, render: (row) => row.email },
        { key: "job", header: t("POSITION"), sortable: true, render: (row) => row.job },
        {
          key: "startDate",
          header: t("JOB START DATE"),
          sortable: true,
          render: (row) => (row.startDate ? moment(row.startDate).format("LL") : ""),
        },
      ],
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [t]
    );

    const externalColumns: DataTableColumn<SSOExternalUserGridListDto>[] = useMemo(
      () => [
        {
          key: "actions",
          header: t("ACTIONS"),
          width: isAdmin ? 200 : 92,
          render: (row) => (
            <div className="sso-rd__table-actions">
              <button
                type="button"
                className="sso-rd__icon-btn sso-rd__icon-btn--login"
                onClick={() => openExternal(row)}
                title={t("Login as this user")}
                aria-label={t("Login as this user")}
              >
                <IconLogin size={16} />
              </button>
              {isAdmin && (
                <>
                  <button
                    type="button"
                    className="sso-rd__icon-btn sso-rd__icon-btn--neutral"
                    onClick={() => {
                      setEditTarget(row);
                      setIsFormOpen(true);
                    }}
                    title={t("Edit")}
                    aria-label={t("Edit")}
                  >
                    <IconPencil size={16} />
                  </button>
                  <button
                    type="button"
                    className="sso-rd__icon-btn"
                    onClick={() => setPendingDelete(row)}
                    title={t("Delete")}
                    aria-label={t("Delete")}
                  >
                    <IconTrash size={16} />
                  </button>
                  <button
                    type="button"
                    className="sso-rd__icon-btn sso-rd__icon-btn--role"
                    onClick={() => setRoleUser(row)}
                    title={t("Assign Role")}
                    aria-label={t("Assign Role")}
                  >
                    <IconUserPlus size={16} />
                  </button>
                </>
              )}
            </div>
          ),
        },
        {
          key: "nameSurname",
          header: t("FULL NAME"),
          sortable: true,
          render: (row) => (
            <span className="sso-rd__table-strong">{row.nameSurname}</span>
          ),
        },
        { key: "email", header: t("EMAIL"), sortable: true, render: (row) => row.email },
        {
          key: "phoneNumber",
          header: t("PHONE NUMBER"),
          sortable: true,
          render: (row) => row.phoneNumber,
        },
        {
          key: "username",
          header: t("USERNAME"),
          sortable: true,
          render: (row) => row.username,
        },
      ],
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [t, isAdmin]
    );

    const users = store!.users;
    const externalUsers = store!.externalusers;

    return (
      <div className="sso-rd__container">
        <div className="sso-rd__page-head">
          <div className="sso-rd__page-head-text">
            <div className="sso-rd__eyebrow">{t("Company Name")}</div>
            <h1 className="sso-rd__page-title">{t("Login as user")}</h1>
          </div>

          {/* Ekleme yalnızca harici kullanıcılar için anlamlı: Securitas
              personeli AX'ten geliyor, buradan oluşturulmuyor. */}
          {tab === "external" && isAdmin && (
            <button
              type="button"
              className="sso-rd__btn sso-rd__btn--primary"
              onClick={() => {
                setEditTarget(undefined);
                setIsFormOpen(true);
              }}
            >
              <IconPlus size={17} />
              {t("Add")}
            </button>
          )}
        </div>

        <div className="sso-rd__tabs">
          <button
            type="button"
            className={"sso-rd__tab" + (tab === "securitas" ? " is-active" : "")}
            onClick={() => setTab("securitas")}
          >
            {t("Securitas Users")}
          </button>
          <button
            type="button"
            className={"sso-rd__tab" + (tab === "external" ? " is-active" : "")}
            onClick={() => setTab("external")}
          >
            {t("External Users")}
          </button>
        </div>

        {/* `key` şart: iki dal da aynı bileşen tipini aynı konumda render ettiği
            için React örneği yeniden kullanır, tablo kendi durumunu (sayfa,
            arama) korur ve yeni sekmenin verisini HİÇ istemez. Ayrı key ile
            sekme değişimi remount eder, liste 1. sayfadan yüklenir. */}
        {tab === "securitas" ? (
          <DataTable<SSOLoginAsUserGridListDto>
            key="securitas"
            columns={securitasColumns}
            data={users.items}
            totalCount={users.totalCount}
            isLoading={!users.isLoaded}
            fetchData={store?.all}
            rowKey={(row) => row.userId}
          />
        ) : (
          <DataTable<SSOExternalUserGridListDto>
            key="external"
            columns={externalColumns}
            data={externalUsers.items}
            totalCount={externalUsers.totalCount}
            isLoading={!externalUsers.isLoaded}
            fetchData={store?.allExternal}
            rowKey={(row) => row.id}
            reloadToken={reloadToken}
          />
        )}

        {isModalOpen && (
          <ProxyLoginModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            user={selectedUser}
            externalUser={selectedExternal}
            isExternal={tab === "external"}
          />
        )}

        {isFormOpen && (
          <ExternalUserFormModal
            isOpen={isFormOpen}
            onClose={() => setIsFormOpen(false)}
            selected={editTarget}
            onSaved={() => {
              setIsFormOpen(false);
              reload();
            }}
          />
        )}

        {roleUser && (
          <RoleAssignmentModal
            isOpen={!!roleUser}
            onClose={() => setRoleUser(undefined)}
            user={roleUser}
          />
        )}

        <ConfirmModal
          isOpen={!!pendingDelete}
          onClose={() => setPendingDelete(undefined)}
          onConfirm={confirmDelete}
          isBusy={isDeleting}
        />
      </div>
    );
    }
  )
) as FC<Props>;

export default LoginAsUserView;
